import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type {
  Company,
  CompanyDepartment,
  CompanyDomain,
  CompanyMember,
  CompanySettings,
} from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DataTable } from '@/app/components/ui/DataTable';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useMe } from '@/features/me/MeContext';
import { RemoveMembershipButton } from '@/features/users/memberActions';
import { useAsync } from '@/lib/useAsync';

import { OrganizationHealthCard } from './OrganizationHealthCard';

export function CompanyDetailPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const { permissions } = useMe();

  const company = useAsync(
    () => apiGet('/companies/{companyId}', { params: { companyId } }) as Promise<Company>,
    [companyId],
  );
  const departments = useAsync(
    () =>
      apiGet('/companies/{companyId}/departments', {
        params: { companyId },
      }) as Promise<CompanyDepartment[]>,
    [companyId],
  );

  const showInternals = permissions.canViewCompanyInternals(companyId);

  return (
    <section className="page flex flex-col gap-6">
      <AsyncSection state={company} label={t('companies.detail.thisCompany')}>
        {(data) => (
          <PageHeader
            title={data.name}
            actions={
              <>
                {permissions.canUpdateCompany(companyId) && (
                  <Button as={Link} variant="secondary" to={`/manage/companies/${companyId}/edit`}>
                    {t('companies.detail.rename')}
                  </Button>
                )}
                {permissions.canGrantRoles && (
                  <Button as={Link} variant="secondary" to={`/manage/companies/${companyId}/roles`}>
                    {t('companies.detail.companyAdmins')}
                  </Button>
                )}
                {permissions.canDeleteCompany && (
                  <Button as={Link} variant="danger" to={`/manage/companies/${companyId}/delete`}>
                    {t('companies.detail.delete')}
                  </Button>
                )}
              </>
            }
          />
        )}
      </AsyncSection>

      <Card padding="none" aria-label={t('companies.detail.departmentsTitle')}>
        <div className="p-5 pb-0">
          <CardHeader
            title={t('companies.detail.departmentsTitle')}
            action={
              permissions.canManageDepartments(companyId) ? (
                <Button
                  as={Link}
                  size="sm"
                  to={`/manage/companies/${companyId}/departments/new`}
                  state={{ from: `/manage/companies/${companyId}` }}
                >
                  {t('companies.detail.newDepartment')}
                </Button>
              ) : undefined
            }
          />
        </div>
        <AsyncSection
          state={departments}
          label={t('companies.detail.departmentsLabel')}
          skeleton="table"
        >
          {(rows) =>
            rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('companies.detail.departmentsEmpty')} />
              </div>
            ) : (
              <DataTable
                rowKey={(department) => department.id ?? ''}
                rows={rows}
                columns={[
                  {
                    key: 'name',
                    header: t('companies.detail.name'),
                    render: (department) => (
                      <Link to={`/departments/${department.id}`}>{department.name}</Link>
                    ),
                  },
                  {
                    key: 'statistics',
                    header: t('companies.detail.statistics'),
                    render: (department) => (
                      <Badge tone="outline">{department.stats_visibility}</Badge>
                    ),
                  },
                  {
                    key: 'collectiveConsent',
                    header: t('companies.detail.collectiveConsent'),
                    render: (department) =>
                      department.collective_consent ? t('companies.detail.inForce') : '—',
                  },
                ]}
              />
            )
          }
        </AsyncSection>
      </Card>

      {showInternals && <CompanyInternals companyId={companyId} edition={company.data?.edition} />}
    </section>
  );
}

/**
 * Settings, domains and members — visible to an admin or this company's
 * admin. Fetched only when the caller may see them, so a plain member's page
 * never even fires the requests that would 403.
 */
function CompanyInternals({
  companyId,
  edition,
}: {
  companyId: string;
  edition?: string | undefined;
}) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const settings = useAsync(
    () =>
      apiGet('/companies/{companyId}/settings', {
        params: { companyId },
      }) as Promise<CompanySettings>,
    [companyId],
  );
  const domains = useAsync(
    () =>
      apiGet('/companies/{companyId}/domains', {
        params: { companyId },
      }) as unknown as Promise<CompanyDomain[]>,
    [companyId],
  );
  const members = useAsync(
    () =>
      apiGet('/companies/{companyId}/users', {
        params: { companyId },
      }) as unknown as Promise<CompanyMember[]>,
    [companyId],
  );

  return (
    <>
      <OrganizationHealthCard
        companyId={companyId}
        edition={edition}
        memberCount={members.data?.length}
      />

      <Card aria-label={t('companies.detail.settingsTitle')}>
        <CardHeader
          title={t('companies.detail.settingsTitle')}
          action={
            permissions.canUpdateCompanySettings ? (
              <Button as={Link} size="sm" to={`/manage/companies/${companyId}/settings`}>
                {t('companies.detail.change')}
              </Button>
            ) : undefined
          }
        />
        <AsyncSection state={settings} label={t('companies.detail.settingsLabel')}>
          {(data) => (
            <p className="text-ink text-sm">
              {t('companies.detail.minGroupSizeLabel')}{' '}
              <strong>{data.effective_min_group_size}</strong>{' '}
              <Badge tone="outline">
                {data.min_group_size === null
                  ? t('companies.detail.systemDefault')
                  : t('companies.detail.companyOverride')}
              </Badge>
            </p>
          )}
        </AsyncSection>
      </Card>

      <Card aria-label={t('companies.detail.domainsTitle')}>
        <CardHeader
          title={t('companies.detail.domainsTitle')}
          action={
            permissions.canUpdateCompanyDomains ? (
              <Button as={Link} size="sm" to={`/manage/companies/${companyId}/domains`}>
                {t('companies.detail.manage')}
              </Button>
            ) : undefined
          }
        />
        <AsyncSection state={domains} label={t('companies.detail.domainsLabel')}>
          {(rows) =>
            rows.length === 0 ? (
              <EmptyState text={t('companies.detail.domainsEmpty')} />
            ) : (
              <ul className="flex flex-col gap-2">
                {rows.map((row) => (
                  <li key={row.id} className="text-ink text-sm">
                    {row.domain}
                  </li>
                ))}
              </ul>
            )
          }
        </AsyncSection>
      </Card>

      <Card padding="none" aria-label={t('companies.detail.membersTitle')}>
        <div className="p-5 pb-0">
          <CardHeader
            title={t('companies.detail.membersTitle')}
            action={
              permissions.canProvisionInCompany(companyId) ? (
                <Button as={Link} size="sm" to={`/manage/companies/${companyId}/members/new`}>
                  {t('companies.detail.addPerson')}
                </Button>
              ) : undefined
            }
          />
        </div>
        <AsyncSection state={members} label={t('companies.detail.membersLabel')} skeleton="table">
          {(rows) =>
            rows.length === 0 ? (
              <div className="p-5 pt-0">
                <EmptyState text={t('companies.detail.membersEmpty')} />
              </div>
            ) : (
              <DataTable
                rowKey={(member) => member.user_id}
                rows={rows}
                columns={[
                  {
                    key: 'name',
                    header: t('companies.detail.name'),
                    render: (member) => <Link to={`/users/${member.user_id}`}>{member.name}</Link>,
                  },
                  {
                    key: 'email',
                    header: t('companies.detail.email'),
                    render: (member) => member.email,
                  },
                  {
                    key: 'login',
                    header: t('companies.detail.login'),
                    render: (member) =>
                      member.has_login ? t('companies.detail.yes') : t('companies.detail.no'),
                  },
                  {
                    key: 'source',
                    header: t('companies.detail.source'),
                    render: (member) => member.source,
                  },
                  {
                    key: 'actions',
                    header: t('companies.detail.actions'),
                    align: 'end',
                    render: (member) =>
                      // Inviting, changing an address, unlinking and erasing
                      // are the person's own affair and live on their page.
                      // Ending a membership stays here: it is the one action
                      // that means something only in this company's context.
                      permissions.isAdmin ? (
                        <RemoveMembershipButton
                          companyId={companyId}
                          userId={member.user_id}
                          onDone={members.reload}
                        />
                      ) : null,
                  },
                ]}
              />
            )
          }
        </AsyncSection>
      </Card>
    </>
  );
}
